// Локальный буфер чанков: каждый чанк сначала в IndexedDB, потом на сервер.

export interface StoredChunk {
  key: string; // `${segmentId}:${seq}` (padded — для сортировки)
  episodeId: number;
  participantId: number;
  segmentId: number;
  segmentNo: number;
  seq: number;
  nSamples: number;
  sampleRate: number;
  status: 'local' | 'verified';
  blob: Blob;
  createdAt: number;
}

const DB_NAME = 'besedka-rec';
const STORE = 'chunks';

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      const store = db.createObjectStore(STORE, { keyPath: 'key' });
      store.createIndex('status', 'status');
      store.createIndex('segment', ['segmentId', 'status']);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

export function chunkKey(segmentId: number, seq: number): string {
  return `${String(segmentId).padStart(10, '0')}:${String(seq).padStart(6, '0')}`;
}

function tx<T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const t = db.transaction(STORE, mode);
        const req = fn(t.objectStore(STORE));
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      })
  );
}

export async function putChunk(chunk: StoredChunk): Promise<void> {
  await tx('readwrite', (s) => s.put(chunk));
}

export async function markVerified(key: string): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const t = db.transaction(STORE, 'readwrite');
    const store = t.objectStore(STORE);
    const get = store.get(key);
    get.onsuccess = () => {
      const chunk = get.result as StoredChunk | undefined;
      if (chunk) {
        chunk.status = 'verified';
        store.put(chunk);
      }
      resolve();
    };
    get.onerror = () => reject(get.error);
  });
}

/** Все незалитые чанки (включая от прошлых заходов/крашей), старейшие первыми. */
export async function getLocalChunks(): Promise<StoredChunk[]> {
  const all = await tx<StoredChunk[]>('readonly', (s) => s.index('status').getAll('local'));
  return all.sort((a, b) => a.createdAt - b.createdAt || a.key.localeCompare(b.key));
}

export async function countLocal(): Promise<{ count: number; oldestAgeMs: number }> {
  const all = await getLocalChunks();
  return {
    count: all.length,
    oldestAgeMs: all.length ? Date.now() - all[0].createdAt : 0
  };
}

/** Держим хвост из N последних verified-чанков сегмента, остальные удаляем. */
export async function pruneVerified(segmentId: number, keepTail: number): Promise<void> {
  const verified = await tx<StoredChunk[]>('readonly', (s) =>
    s.index('segment').getAll([segmentId, 'verified'])
  );
  verified.sort((a, b) => a.seq - b.seq);
  const excess = verified.slice(0, Math.max(0, verified.length - keepTail));
  if (!excess.length) return;
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const t = db.transaction(STORE, 'readwrite');
    const store = t.objectStore(STORE);
    for (const chunk of excess) store.delete(chunk.key);
    t.oncomplete = () => resolve();
    t.onerror = () => reject(t.error);
  });
}
